import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import Header from "../../../components/common/header";
import { Order } from "../../../__generated__/graphql";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { notify, notifyError } from "../../../utils/toast";
import { formatDateToYearMonthDayDayOfWeek } from "../../../utils/date/format-date";
import {
  getStoreByOrder,
  getPickUpTimeByOrder,
  getOrderType,
  getPickUpDateByOrder,
  getOrderDateStatus,
} from "../../../utils/order";
import { OrderStatus } from "../../../components/order/order-status";
import OrderItems from "../../../components/order/order-items";
import { HiChevronRight } from "react-icons/hi2";
import OrderNotice from "../../../components/order/order-notice";
import OrderStatusBar from "../../../components/order/order-status-bar";
import { TOGGLE_PICKUP_COMPLETED } from "../../../api/order-mutations";
import LoadingSpinnerFull from "../../../components/common/loading-spinner/loading-spinner-full";
import * as Sentry from "@sentry/react";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";

export const GET_ORDER_DETAIL = gql`
  query Order($id: Int!) {
    order(id: $id) {
      id
      pickUpTime
      packPickupStartTime
      packPickupEndTime
      packAutoConfirmTime
      createdAt
      amount
      baseAmount
      couponDiscountAmount
      isApproved
      isPickupCompleted
      products {
        id
        img
        name
        price
        userPrice
        quantity
        discountRate
        store {
          id
          title
          img
          isDeleted
          isHidden
          address
        }
        menus {
          img
          menu {
            name
            img
            price
          }
          quantity
        }
      }
      pack {
        store {
          id
          title
          img
          reserveTime
          isDeleted
          isHidden
          address
        }
        price
        userPrice
      }
      packId
      totalDiscount
      totalQuantity
      isPackConfirmed
      isCanceled
      canceledBy
      pickUpTime
      orderStatus
      identificationCode
      userCoupon {
        coupon {
          title
        }
      }
    }
  }
`;

const CANCEL_ORDER_MUTATION = gql`
  mutation CancelOrderByUser($orderId: Int!, $cancelReason: String!) {
    cancelPackOrderByUser(orderId: $orderId, cancelReason: $cancelReason) {
      ok
      error
    }
  }
`;

const canCancelOrder = (order: Order) => {
  if (!order.packId || !order.pack?.store?.reserveTime) return false;

  const today = new Date();
  const pickUpDate = new Date(order?.packPickupStartTime || "");
  const dayOfWeek = ((today.getDay() + 6) % 7).toString();
  const tomorrowDayOfWeek = ((today.getDay() + 7) % 7).toString();
  const reserveTime = order.pack?.store?.reserveTime[dayOfWeek];
  const tomorrowReserveTime = order.pack?.store?.reserveTime[tomorrowDayOfWeek];

  const [startHour, startMinute] = reserveTime.start.split(":").map(Number);
  const [tomorrowStartHour, tomorrowStartMinute] = tomorrowReserveTime.start
    .split(":")
    .map(Number);

  const todayReserveStart = new Date(today);
  todayReserveStart.setHours(startHour, startMinute - 30, 0, 0);

  const tomorrowReserveStart = new Date(today);
  tomorrowReserveStart.setDate(today.getDate() + 1);
  tomorrowReserveStart.setHours(
    tomorrowStartHour,
    tomorrowStartMinute - 30,
    0,
    0
  );

  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (
    today.getFullYear() === pickUpDate.getFullYear() &&
    today.getMonth() === pickUpDate.getMonth() &&
    today.getDate() === pickUpDate.getDate()
  ) {
    return (
      today < todayReserveStart && !order.isCanceled && !order.isPackConfirmed
    );
  } else if (
    tomorrow.getFullYear() === pickUpDate.getFullYear() &&
    tomorrow.getMonth() === pickUpDate.getMonth() &&
    tomorrow.getDate() === pickUpDate.getDate()
  ) {
    return (
      today < tomorrowReserveStart &&
      !order.isCanceled &&
      !order.isPackConfirmed
    );
  }
};

const OrderDetail = () => {
  const { id } = useParams();
  const { data, loading, error, refetch } = useQuery(GET_ORDER_DETAIL, {
    variables: { id: Number(id) },
    fetchPolicy: "network-only",
  });
  const navigate = useNavigate();

  const [cancelOrder] = useMutation(CANCEL_ORDER_MUTATION);
  const [togglePickupCompleted] = useMutation(TOGGLE_PICKUP_COMPLETED);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>("");
  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
  const [isPickupLoading, setIsPickupLoading] = useState(false);
  const [showPickupNotice, setShowPickupNotice] = useState(() => {
    return !localStorage.getItem("user_pickup_notice_seen");
  });

  if (loading) return <LoadingSpinnerFull />;
  if (error) return <div>Error: {error.message}</div>;

  const order = data?.order;
  const store = getStoreByOrder(order);

  const handleCancelOrder = async () => {
    if (!cancelReason) {
      alert("취소 사유를 선택해주세요.");
      return;
    }
    if (cancelReason.length < 4) {
      notifyError("취소 사유는 4글자 이상 입력해주세요.");
      return;
    }
    try {
      await cancelOrder({
        variables: {
          orderId: order.id,
          cancelReason,
        },
        onCompleted: (data) => {
          if (data.cancelPackOrderByUser.ok) {
            notify("주문이 취소되었습니다.");
            refetch();
          }
        },
        onError: (error) => {
          notifyError(error.message);
          refetch();
        },
      });
      setIsModalOpen(false);
      return;
    } catch (error) {
      console.error("Error canceling order:", error);
      alert("주문 취소 중 오류가 발생했습니다.");
    }
  };

  const handlePickupComplete = async () => {
    setIsPickupLoading(true);
    try {
      await togglePickupCompleted({
        variables: { id: order.id, triggeredBy: "user" },
        update: (cache) => {
          // 캐시에서 현재 order를 찾아서 업데이트
          const existingOrder = cache.readQuery<{
            order: Order;
          }>({
            query: GET_ORDER_DETAIL,
            variables: { id: Number(id) },
          });

          if (existingOrder?.order) {
            cache.writeQuery<{
              order: Order;
            }>({
              query: GET_ORDER_DETAIL,
              variables: { id: Number(id) },
              data: {
                order: {
                  ...existingOrder.order,
                  isPickupCompleted: !existingOrder.order.isPickupCompleted, // 픽업 완료 상태 변경
                },
              },
            });
          }
        },
      });
      setIsPickupModalOpen(false);
    } catch (error) {
      Sentry.captureException(error);
      alert("픽업 완료 처리 중 오류가 발생했습니다.");
    } finally {
      setIsPickupLoading(false);
    }
  };

  return (
    <div className="overflow-y-scroll h-screen">
      <Header title="주문 상세" showBackButton />

      <div className="p-4 bg-gray-100">
        {/* 주문 상태 바 */}
        <OrderStatusBar order={order} />

        {/* 주문 정보 */}
        <div className="mb-6">
          <div
            className={`bg-white border border-gray-200 ${
              order.identificationCode ? "rounded-t-lg" : "rounded-lg"
            } p-4`}
          >
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">주문 날짜</span>
                <span className="font-medium text-gray-900">
                  {formatDateToYearMonthDayDayOfWeek(new Date(order.createdAt))}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                <span className="text-gray-600">주문 상태</span>
                <div className="font-bold">
                  <OrderStatus order={order} />
                </div>
              </div>
            </div>
          </div>
          {order.identificationCode && (
            <div className="bg-primary/80 px-4 py-3 border rounded-b-lg text-white">
              <div className="flex justify-between items-center">
                <span className="font-medium">사장님 확인 코드</span>
                <span className="font-black tracking-wider text-2xl">
                  {order.identificationCode}
                </span>
              </div>
              {(order.orderStatus.includes("예약팩-주문확정") ||
                order.orderStatus.includes("실시간팩-주문확정")) && (
                <div className="border-t border-white/50 pt-3 mt-3">
                  <div className="text-center">
                    <div className="flex flex-col items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={order.isPickupCompleted}
                          onChange={() => {
                            if (!order.isPickupCompleted) {
                              setIsPickupModalOpen(true);
                            }
                          }}
                        />
                        <div
                          className={`w-[138px] h-16 rounded-full shadow-inner transition-colors duration-300 ${
                            order.isPickupCompleted
                              ? "bg-green-500"
                              : "bg-gray-300"
                          }`}
                        ></div>
                        <div
                          className={`absolute w-12 h-12 bg-white rounded-full shadow-lg transform transition-transform duration-300 ease-in-out ${
                            order.isPickupCompleted
                              ? "translate-x-[82px]"
                              : "translate-x-[6px]"
                          }`}
                        ></div>
                      </label>

                      <div className="text-white text-xl font-bold mt-2">
                        직원 확인 버튼
                      </div>

                      <div className="text-lg text-white break-keep">
                        직원에게 이 화면 보여드리고 눌러달라고 하기!
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 픽업 정보 및 매장 정보*/}
        <h2 className="font-bold mb-1 text-2xl">픽업 정보</h2>
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-4 border-b border-gray-200 pb-4">
            <ResponsiveCDNImage
              src={store?.img || ""}
              alt={store?.title || ""}
              className="w-16 h-16 object-cover rounded-lg"
              sizeRatio={1 / 6}
            />
            <div className="flex-1">
              <div
                className={`flex items-center ${
                  !store?.isDeleted && !store?.isHidden && "cursor-pointer"
                }`}
                onClick={() => {
                  if (!store?.isDeleted && !store?.isHidden) navigate(`/store/${store?.id}`);
                }}
              >
                <span className="font-bold text-xl text-gray-900 underline underline-offset-4 break-keep">
                  {store?.title}
                </span>
                {!store?.isDeleted && !store?.isHidden && (
                  <HiChevronRight className="w-6 h-6 ml-0.5 text-gray-500" />
                )}
              </div>
              <div className="text-gray-600 mt-1">{store?.address}</div>
            </div>
          </div>

          {/* 픽업 날짜 및 시간 */}
          <div className="space-y-2 pt-4">
            <div className="border-b border-gray-200 pb-4">
              <div className="text-gray-600">픽업 날짜</div>
              <div className="text-center space-y-1">
                <div className="text-2xl font-black text-gray-900 tracking-wide">
                  {getPickUpDateByOrder(order)}{" "}
                  {getOrderDateStatus(order) && (
                    <span className="text-primary/80 font-semibold bg-primary/10 px-2 py-1 rounded-full">
                      {getOrderDateStatus(order)}
                    </span>
                  )}
                </div>
                <div className="text-4xl font-black text-primary tracking-tight break-keep">
                  {getPickUpTimeByOrder(order)}
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-primary/90 rounded-lg">
            <OrderNotice order={order} />
          </div>
        </div>

        <h2 className="font-bold mb-1 text-2xl">마히팩 구성</h2>
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">주문 유형</span>
              <span className="font-semibold border border-gray-200 bg-gray-50 px-2 py-1 rounded-full text-gray-900">
                {getOrderType(order)}
              </span>
            </div>
            <div className="pt-2 border-t border-gray-100">
              <OrderItems order={order} />
            </div>
          </div>
        </div>

        <h2 className="font-bold mb-1 text-2xl">결제 정보</h2>
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-10">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">총 금액</span>
              <span className="font-medium text-gray-900">
                {(
                  (order.baseAmount || order.amount) +
                  (order.totalDiscount || 0)
                )?.toLocaleString()}
                원
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <span className="text-gray-600">총 할인 금액</span>
              <span className="font-medium text-red-600">
                -
                {(
                  order.totalDiscount + (order.couponDiscountAmount || 0)
                )?.toLocaleString()}
                원
              </span>
            </div>
            {order.userCoupon && (
              <>
                <div className="flex justify-between items-center text-md">
                  <span className="text-gray-500">마히 할인</span>
                  <span className="text-red-600">
                    -{order.totalDiscount.toLocaleString()}원
                  </span>
                </div>
                <div className="flex justify-between items-start text-md">
                  <span className="text-gray-500 break-keep">
                    쿠폰 할인: {order.userCoupon?.coupon?.title || ""}
                  </span>
                  <span className="text-red-600">
                    -{order.couponDiscountAmount}원
                  </span>
                </div>
              </>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <span className="text-gray-900 font-bold text-2xl">
                총 결제 금액
              </span>
              <span className="text-gray-900 font-black text-2xl">
                {order.amount?.toLocaleString()}원
              </span>
            </div>
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="space-y-3 mt-6">
          {canCancelOrder(order) && (
            <button
              className="w-full p-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors"
              onClick={() => setIsModalOpen(true)}
            >
              주문 취소
            </button>
          )}

          <a
            href="http://pf.kakao.com/_xawxcRn/chat"
            className="w-full p-4 text-primary underline underline-offset-4 font-medium flex items-center justify-center gap-2 hover:text-primary/80 transition-colors"
          >
            혹시 문의사항이 있으신가요?
          </a>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4">
          <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-lg">
            <h2 className="text-2xl font-bold mb-6">
              주문 취소 전에 간단한 안내 드려요 😊
            </h2>
            <p className="text-gray-600 mb-6 break-keep">
              주문 취소 시 취소 사유가 <strong>매장에 전달</strong>됩니다.
              양해해 주시는 마음으로 취소 사유를 남겨주시면 감사하겠습니다.
            </p>
            <p className="text-gray-600 mb-6 break-keep underline underline-offset-4">
              취소 시 환불은 영업일 기준 1-2일 소요됩니다.
            </p>
            <textarea
              className="w-full p-4 border-2 border-gray-200 rounded-lg mb-6 text-lg"
              placeholder="취소하시는 이유를 입력해 주세요"
              rows={4}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            <div className="flex justify-end gap-3">
              <button
                className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg text-lg font-medium hover:bg-gray-200"
                onClick={() => setIsModalOpen(false)}
              >
                돌아가기
              </button>
              <button
                className="bg-red-600 text-white px-6 py-3 rounded-lg text-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleCancelOrder}
                disabled={!cancelReason}
              >
                취소하기
              </button>
            </div>
          </div>
        </div>
      )}

      {isPickupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
          <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-lg">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-3">
                직원 확인용 픽업 완료 버튼
              </h2>

              <p className="text-gray-600 text-xl leading-relaxed break-keep">
                이 버튼을 누르면{" "}
                <span className="font-bold italic">사장님 판매 현황 </span>에서{" "}
                <span className="font-bold">픽업 완료</span>를 확인할 수 있어요!
              </p>
            </div>

            <div className="flex justify-center gap-4 w-full">
              <button
                className="bg-gray-100 text-gray-700 py-4 rounded-lg text-lg font-medium hover:bg-gray-200 transition-colors w-1/2"
                onClick={() => setIsPickupModalOpen(false)}
                disabled={isPickupLoading}
              >
                취소
              </button>
              <button
                className="bg-blue-500 text-white py-4 rounded-lg text-lg font-medium hover:bg-blue-600 transition-colors w-1/2"
                onClick={handlePickupComplete}
                disabled={isPickupLoading}
              >
                {isPickupLoading ? <LoadingSpinnerInline /> : "픽업 완료"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPickupNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
          <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-lg">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-gray-800 mb-3 break-keep">
                '직원 확인 버튼'에 대해 알려드려요
              </h2>

              <div className="bg-primary/80 px-4 py-3 border rounded-b-lg text-white">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-md">사장님 확인 코드</span>
                  <span className="font-black tracking-wider text-xl">
                    맑은 히어로
                  </span>
                </div>
                <div className="border-t border-white/50 pt-3 mt-3">
                  <div className="text-center">
                    <div className="flex flex-col items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={false}
                        />
                        <div
                          className={`w-[138px] h-16 rounded-full shadow-inner transition-colors duration-300 bg-gray-300`}
                        ></div>
                        <div
                          className={`absolute w-12 h-12 bg-white rounded-full shadow-lg transform transition-transform duration-300 ease-in-out translate-x-[6px]`}
                        ></div>
                      </label>

                      <div className="text-white text-lg font-bold mt-2">
                        직원 확인 버튼
                      </div>

                      <div className="text-white text-md break-keep">
                        직원에게 이 화면 보여드리고 눌러달라고 하기!
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                <p className="text-gray-800 text-lg leading-tight break-keep">
                  이제 주문이 확정되어 매장에 픽업을 하러 갔을 때 매장의
                  직원에게 이 화면을 보여주세요.
                </p>
                <p className="text-gray-800 text-lg leading-tight break-keep">
                  그러면 직원이 확인 후에 버튼을 눌러줄 거예요.
                </p>
              </div>
            </div>

            <div className="flex justify-center gap-4 w-full">
              <button
                className="bg-blue-500 text-white px-6 py-3 rounded-lg text-lg font-medium hover:bg-blue-600 transition-colors w-1/2"
                onClick={() => {
                  localStorage.setItem("user_pickup_notice_seen", "true");
                  setShowPickupNotice(false);
                }}
              >
                다시보지 않기
              </button>
              <button
                className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg text-lg font-medium hover:bg-gray-200 transition-colors w-1/2"
                onClick={() => {
                  setShowPickupNotice(false);
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;
